/**
 * Google Drive Service
 *
 * Fetches housing data from a public Google Drive folder structure:
 *   Root Folder (public)
 *   ├── District 1/
 *   │   ├── Property Name/
 *   │   │   ├── image1.jpg
 *   │   │   ├── image2.jpg
 *   │   │   ├── property_original.txt  (plain text reference copy)
 *   │   │   └── property.txt            (encrypted: base64 + XOR cipher)
 *   │   └── Another Property/
 *   ├── District 7/
 *   └── ...
 *
 * property.txt is encrypted using XOR cipher + base64 encoding.
 * The browser downloads the raw encrypted content via /api/drive-text proxy,
 * then decrypts it CLIENT-SIDE using crypto.js + REACT_APP_SEED_KEY.
 * This ensures the seed key never leaves the client.
 *
 * Uses Google Drive API v3 with an API key (no OAuth needed for public folders).
 */

import config from '../config';
import { decrypt } from '../utils/crypto';

// In-memory cache to avoid repeated API calls on re-renders / page navigation
let cache = {
  data: null,
  timestamp: 0,
};

/**
 * Fetch all files/folders recursively from a Drive folder.
 * Uses the free public API (Google Drive API v3) with file size limits.
 * For large folders (>1000 files), pagination is supported.
 */
export async function listFiles(folderId, apiKey, pageToken = null) {
  const baseUrl = 'https://www.googleapis.com/drive/v3/files';
  const query = encodeURIComponent(
    `'${folderId}' in parents and trashed=false`
  );
  const fields =
    'files(id,name,mimeType,size,webViewLink,thumbnailLink,imageMediaMetadata),nextPageToken';
  const url = `${baseUrl}?q=${query}&fields=${fields}&key=${apiKey}&pageSize=1000${
    pageToken ? `&pageToken=${pageToken}` : ''
  }`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(
      `Google Drive API error: ${response.status} ${response.statusText}`
    );
  }
  return response.json();
}

/**
 * Download and read text file content from Google Drive with retry.
 *
 * For property.txt, the raw encrypted content is downloaded (base64 encoded string).
 * Decryption happens client-side using the seed key from config.
 *
 * Google Drive has a replication delay after a file is created/updated via OAuth
 * before the content becomes available via the public API key. This function
 * retries with 1.5s delays when content is empty to handle this delay.
 *
 * @param {string} fileId - Google Drive file ID
 * @param {string} apiKey - Google Drive API key
 * @returns {string} Raw file content (encrypted for property.txt, or plain text)
 */
/**
 * Simple promise-based rate limiter: runs async functions with a max concurrency.
 */
class ConcurrencyLimiter {
  constructor(maxConcurrent) {
    this.maxConcurrent = maxConcurrent;
    this.active = 0;
    this.queue = [];
  }

  async run(fn) {
    if (this.active >= this.maxConcurrent) {
      await new Promise(resolve => this.queue.push(resolve));
    }
    this.active++;
    try {
      return await fn();
    } finally {
      this.active--;
      if (this.queue.length > 0) {
        const next = this.queue.shift();
        next();
      }
    }
  }
}

// Global concurrency limiter: max 4 parallel Drive text file requests
const textFileLimiter = new ConcurrencyLimiter(4);

export async function readTextFile(fileId, apiKey) {
  return textFileLimiter.run(async () => {
    const url = `/api/drive-text?id=${fileId}&key=${apiKey}`;

    // Retry up to 10 times with exponential backoff (handles 403 rate limits + replication lag)
    let lastError = null;
    for (let attempt = 0; attempt < 10; attempt++) {
      try {
        const response = await fetch(url);
        if (response.ok) {
          const text = await response.text();
          if (text && text.trim().length > 0) {
            return text;
          }
          lastError = 'Empty content received';
        } else {
          const body = await response.text();
          let errorMsg;
          try {
            const parsed = JSON.parse(body);
            errorMsg = parsed.error || parsed.detail || body;
          } catch {
            errorMsg = body || `HTTP ${response.status}`;
          }
          lastError = `Failed to read file ${fileId}: ${errorMsg}`;

          // On 403 (rate limit) or 5xx, retry with backoff; on 404 throw immediately
          if (response.status !== 403 && response.status < 500) {
            throw new Error(lastError);
          }
        }
      } catch (err) {
        lastError = err.message;
        // If it's a non-retryable error (not 403/5xx), re-throw
        if (err.message && !err.message.includes('403') && !err.message.includes('5') && !err.message.includes('Empty')) {
          throw err;
        }
      }
      // Exponential backoff with jitter: 1.5s, 3s, 6s, 12s, ... (capped at ~20s)
      const delay = Math.min(1500 * Math.pow(2, attempt) + Math.random() * 1000, 20000);
      await new Promise(resolve => setTimeout(resolve, delay));
    }

    throw new Error(lastError || `Failed to read file ${fileId} after multiple retries`);
  });
}

/**
 * Get a displayable URL for a Google Drive image.
 */
export function getImageUrl(fileId) {
  return `/api/drive-image?id=${fileId}`;
}

/**
 * Helper: check if a file is an image.
 */
function isImageFile(f) {
  return (
    f.mimeType &&
    (f.mimeType.startsWith('image/') ||
      f.name.match(/\.(jpg|jpeg|png|gif|webp)$/i))
  );
}

/**
 * Helper: check if a file is property.txt (the encrypted property data file).
 * Returns only property.txt, NOT property_original.txt which is the plain text reference copy.
 */
function isPropertyTextFile(f) {
  return f.name === 'property.txt';
}

/**
 * Fully parallel Drive structure loader with streaming support.
 *
 * Properties appear incrementally via the onProperty callback as each
 * property.txt finishes downloading. All text files within each district
 * are fetched in parallel.
 *
 * Decryption happens CLIENT-SIDE: the raw encrypted content from property.txt
 * is decrypted using the seed key (REACT_APP_SEED_KEY) via crypto.js XOR decrypt.
 *
 * @param {string} apiKey
 * @param {string} rootFolderId
 * @param {Function} onProperty - Callback(driveDistrict, drivePropertyObj) called as each property finishes
 * @returns {Promise<{districts: Array}>} Full structure (same format as before)
 */
export async function fetchDriveStructure(apiKey, rootFolderId, onProperty) {
  if (!apiKey || !rootFolderId) {
    console.warn(
      '[GoogleDrive] Missing API key or root folder ID. Configure in src/config.js'
    );
    return { districts: [] };
  }

  try {
    // Step 1: Get ALL district folders (single API call)
    const rootResponse = await listFiles(rootFolderId, apiKey);
    const districtFolders = (rootResponse.files || []).filter(
      (f) => f.mimeType === 'application/vnd.google-apps.folder'
    );

    console.log(
      `[GoogleDrive] Found ${districtFolders.length} district folders`
    );

    // Step 2: Fetch ALL property folders for ALL districts in PARALLEL
    const districtListPromises = districtFolders.map(async (districtFolder) => {
      const districtResponse = await listFiles(districtFolder.id, apiKey);
      const propertyFolders = (districtResponse.files || []).filter(
        (f) => f.mimeType === 'application/vnd.google-apps.folder'
      );
      return { districtFolder, propertyFolders };
    });

    const districtResults = await Promise.all(districtListPromises);

    // Step 3: For each district, fetch property file listings and read text files in PARALLEL
    const districtDataPromises = districtResults.map(async ({ districtFolder, propertyFolders }) => {
      // Fetch ALL property file listings in parallel
      const propFileListPromises = propertyFolders.map(pf =>
        listFiles(pf.id, apiKey).catch(() => ({ files: [] }))
      );
      const propFileResults = await Promise.all(propFileListPromises);

      // Build the list of read tasks: read only property.txt files in parallel
      const propertyTasks = propertyFolders.map((propertyFolder, idx) => {
        const files = propFileResults[idx].files || [];
        const imageFiles = files.filter(isImageFile);
        const textFiles = files.filter(isPropertyTextFile);
        const textFileId = textFiles.length > 0 ? textFiles[0].id : null;

        // Kick off text file read immediately (parallel) - download raw encrypted content
        const textPromise = textFileId
          ? readTextFile(textFileId, apiKey).catch(err => {
              console.warn(
                `[GoogleDrive] Failed to read text file for ${propertyFolder.name}:`,
                err
              );
              return '';
            })
          : Promise.resolve('');

        // Return a task that resolves when text content is ready
        return textPromise.then(textContent => {
          // Decrypt property.txt content CLIENT-SIDE:
          // 1. Base64 decode + XOR decrypt using the seed key from config
          const seedKey = config.seedKey;
          const decodedContent = textContent ? decrypt(textContent, seedKey) : '';

          const driveProperty = {
            id: propertyFolder.id,
            name: propertyFolder.name,
            imageFiles: imageFiles.map((f) => ({
              id: f.id,
              name: f.name,
              url: getImageUrl(f.id),
              thumbnailUrl: getImageUrl(f.id),
            })),
            textContent: decodedContent,
            raw: { districtFolder, propertyFolder, files },
          };

          // Notify listener that a property just finished loading (incremental)
          if (onProperty && (imageFiles.length > 0 || decodedContent)) {
            onProperty(districtFolder, driveProperty);
          }

          return driveProperty;
        });
      });

      // Wait for all property reads in this district to finish
      const properties = await Promise.all(propertyTasks);

      return {
        id: districtFolder.id,
        name: districtFolder.name,
        properties: properties.filter(p => p.imageFiles.length > 0 || p.textContent),
      };
    });

    const districts = await Promise.all(districtDataPromises);

    console.log(
      `[GoogleDrive] Loaded ${districts.reduce((sum, d) => sum + d.properties.length, 0)} properties across ${districts.length} districts`
    );

    return { districts, raw: { rootResponse, districtFolders } };
  } catch (error) {
    console.error('[GoogleDrive] Error fetching Drive structure:', error);
    return { districts: [], error: error.message };
  }
}

/**
 * Clear the in-memory cache.
 */
export function clearCache() {
  cache = { data: null, timestamp: 0 };
  console.log('[GoogleDrive] Cache cleared');
}

/**
 * Fetch data with in-memory caching.
 * Re-fetches only if cache has expired.
 */
export async function fetchDriveData(config) {
  const now = Date.now();
  if (
    cache.data &&
    cache.timestamp > 0 &&
    config.features.cacheDurationMs > 0 &&
    now - cache.timestamp < config.features.cacheDurationMs
  ) {
    console.log('[GoogleDrive] Returning cached data');
    return cache.data;
  }

  const data = await fetchDriveStructure(
    config.googleDrive.apiKey,
    config.googleDrive.rootFolderId
  );

  cache.data = data;
  cache.timestamp = now;
  return data;
}

const GoogleDriveService = {
  listFiles,
  readTextFile,
  getImageUrl,
  fetchDriveStructure,
  fetchDriveData,
  clearCache,
};

export default GoogleDriveService;